import React from "react";
import { Form, useFormikContext } from "formik";

import { DealMetricType } from "@vega-api/generated/types/dealMetricType";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import {
  DropdownField,
  ThemedDropdownField,
} from "@vega-core/components/Form/Controls/Dropdown/Dropdown/DropdownField";
import { InputField } from "@vega-core/components/Form/Fields/InputField";
import { TextareaField } from "@vega-core/components/Form/Fields/TextareaField";
import { type CoreCurrency } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";
import { useDefaultCurrency } from "@vega-core/utils/formatMoney";

import {
  ACS_TEAM_CONTACT_NAME,
  ACS_TEAM_CONTACT_OPTIONS,
  FORM_FIELD_NAMES,
  MODAL_OPTION_VALUES,
  MODAL_TYPES,
  type ModalFormValues,
  type ModalType,
} from "./constants/modalConstants";
import { type CoinvestmentOverviewOption } from "./hooks/usePresetOptions";
import { MetricValueField } from "./MetricValueField";

type Props = {
  modalType: ModalType;
  options: CoinvestmentOverviewOption[];
  onClose: () => void;
  dealCurrency?: CoreCurrency;
};

export const AddMetricSectionForm = ({
  modalType,
  options,
  onClose,
  dealCurrency,
}: Props) => {
  const { submitForm, resetForm } = useFormikContext<ModalFormValues>();

  const handleCancel = () => {
    resetForm();
    onClose();
  };

  return (
    <>
      <AddMetricSectionFormFields
        dealCurrency={dealCurrency}
        modalType={modalType}
        options={options}
      />
      <div className="flex justify-end space-x-3 pt-8">
        <ThemedButton
          dataTestId="cancel-button"
          onClick={handleCancel}
          type="button"
          variant="secondary"
        >
          {m.btn_cancel()}
        </ThemedButton>
        <ThemedButton
          dataTestId="add-button"
          onClick={submitForm}
          type="button"
          variant="primary"
        >
          {m.coInvestments_lpDealOverviewContent_add({ type: modalType })}
        </ThemedButton>
      </div>
    </>
  );
};

const AddMetricSectionFormFields = ({
  modalType,
  options,
  dealCurrency,
}: {
  modalType: ModalType;
  options: CoinvestmentOverviewOption[];
  dealCurrency?: CoreCurrency;
}) => {
  const { values, setFormikState } = useFormikContext<ModalFormValues>();
  const defaultCurrency = useDefaultCurrency();
  const isSection = modalType === MODAL_TYPES.SECTION;
  const selectedOption = values.selectedOption;

  // Find the full selected option to get its metric type
  const selectedOptionObject = options.find(
    (opt) => opt.value === selectedOption,
  );

  const metricType = selectedOptionObject?.metricType;

  React.useEffect(() => {
    if (!selectedOption) return;

    const label =
      selectedOption === MODAL_OPTION_VALUES.CUSTOM ? "" : selectedOption;

    const customValue =
      metricType === DealMetricType.MONEY
        ? { amount: "", currency: dealCurrency ?? defaultCurrency }
        : "";

    // Reset form state for value fields when switching metric types.
    // This is necessary because customValue changes shape between string and { amount, currency },
    // and stale validation errors from the previous type would otherwise persist and display incorrectly.
    setFormikState((prev) => ({
      ...prev,
      values: {
        ...prev.values,
        customLabel: label,
        customValue,
        metricType,
      },
      touched: {
        [FORM_FIELD_NAMES.SELECTED_OPTION]:
          prev.touched[FORM_FIELD_NAMES.SELECTED_OPTION],
      },
      errors: {
        [FORM_FIELD_NAMES.SELECTED_OPTION]:
          prev.errors[FORM_FIELD_NAMES.SELECTED_OPTION],
      },
    }));
  }, [
    selectedOption,
    metricType,
    dealCurrency,
    defaultCurrency,
    setFormikState,
  ]);
  const isACSContactSelected =
    isSection && selectedOptionObject?.label === ACS_TEAM_CONTACT_NAME;

  return (
    <Form className="space-y-6" id="add-metric-section-form">
      <div className="mt-4 text-left">
        <DropdownField
          label={m.coInvestments_lpDealOverviewContent_select({
            type: modalType,
          })}
          name={FORM_FIELD_NAMES.SELECTED_OPTION}
          options={options}
          placeholder={m.coInvestments_lpDealOverviewContent_choose({
            type: modalType,
          })}
        />
      </div>

      {selectedOption && values.metricType === metricType ? (
        <div className="space-y-4">
          {selectedOption === MODAL_OPTION_VALUES.CUSTOM && (
            <InputField
              label={m.coInvestments_lpDealOverviewContent_label()}
              name={FORM_FIELD_NAMES.CUSTOM_LABEL}
              placeholder={m.coInvestments_lpDealOverviewContent_enterCustomLabel()}
            />
          )}
          {isACSContactSelected ? (
            <ThemedDropdownField
              isClearable={false}
              label={m.coInvestments_lpDealOverviewContent_value()}
              name={FORM_FIELD_NAMES.CUSTOM_VALUE}
              options={ACS_TEAM_CONTACT_OPTIONS}
            />
          ) : null}
          {isSection && !isACSContactSelected ? (
            <TextareaField
              label={m.coInvestments_lpDealOverviewContent_value()}
              name={FORM_FIELD_NAMES.CUSTOM_VALUE}
              placeholder={m.coInvestments_lpDealOverviewContent_enterValue()}
              rows={4}
            />
          ) : null}
          {isSection ? null : (
            <MetricValueField
              dealCurrency={dealCurrency}
              editable
              fieldName={FORM_FIELD_NAMES.CUSTOM_VALUE}
              metricType={metricType}
            />
          )}
        </div>
      ) : null}
    </Form>
  );
};
